// Cadastrar/Editar veículo → "Puxar de Vendas" (pedido 2026-10-13): escolhe um
// veículo de um pré-contrato (Vendas, vindo do formulário) e preenche o
// cadastro sozinho — nome "MODELO - PLACA", modelo, marca, ano, placa, cor
// e plano. Nunca mexe em IMEI/chip/rastreador. Só aparece pra quem tem acesso a Vendas (backend decide).
import { useMemo, useState } from "react";
import { Search, ShoppingCart, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { useSalesAccess, useSalesLeads } from "@/hooks/queries/sales";
import { isRestrictedAccountType } from "@/lib/account-type";
import { STAGE_LABELS, type LeadVehicle, type SalesLead } from "@/services/gpsdrive-sales.service";

export interface SalesVehiclePick {
  lead: SalesLead;
  vehicle: LeadVehicle;
}

const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const plateKey = (s: string) => s.replace(/[^a-z0-9]/gi, "").toUpperCase();

export function SalesVehiclePicker({
  onPick,
  currentPlate,
}: {
  onPick: (pick: SalesVehiclePick | null) => void;
  /** Editar dispositivo: veículo de Vendas com a mesma placa vem primeiro. */
  currentPlate?: string;
}) {
  const { gpsDriveUser } = useAuth();
  const allowed = !!gpsDriveUser && !isRestrictedAccountType(gpsDriveUser.accountType);
  const { data: access } = useSalesAccess(allowed);
  const { data: leads, isLoading } = useSalesLeads(allowed && !!access?.view);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<SalesVehiclePick | null>(null);

  // Um item por veículo do pré-contrato (o cliente pode ter vários).
  const options = useMemo(() => {
    const list: Array<SalesVehiclePick & { key: string; haystack: string; position: string }> = [];
    for (const lead of leads ?? []) {
      lead.data.vehicles.forEach((vehicle, i) => {
        if (!vehicle.plate.trim() && !vehicle.model.trim()) return;
        list.push({
          lead,
          vehicle,
          key: `${lead.id}:${i}`,
          position: lead.data.vehicles.length > 1 ? `Veículo ${i + 1} de ${lead.data.vehicles.length} · ` : "",
          haystack: norm(
            [lead.data.name, lead.data.cpfCnpj, lead.data.phone1, vehicle.plate, vehicle.model].join(" "),
          ).replace(/[-\s]/g, ""),
        });
      });
    }
    const mine = currentPlate ? plateKey(currentPlate) : "";
    if (mine) list.sort((a, b) => Number(plateKey(b.vehicle.plate) === mine) - Number(plateKey(a.vehicle.plate) === mine));
    return list;
  }, [leads, currentPlate]);
  const mine = currentPlate ? plateKey(currentPlate) : "";

  if (!allowed || !access?.view) return null;

  const q = norm(query).replace(/[-\s]/g, "");
  const filtered = (q ? options.filter((o) => o.haystack.includes(q)) : options).slice(0, 30);

  const choose = (pick: SalesVehiclePick | null) => {
    setPicked(pick);
    setOpen(false);
    setQuery("");
    onPick(pick);
  };

  return (
    <div className="mb-6 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <ShoppingCart className="h-4 w-4 text-primary" />
        Puxar dados de Vendas
      </div>
      {picked ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
          <div className="min-w-0">
            <div className="truncate font-medium">{picked.lead.data.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {[picked.vehicle.model, picked.vehicle.plate, picked.vehicle.year, picked.vehicle.color]
                .filter(Boolean)
                .join(" · ")}
              {picked.lead.data.plan ? ` · Plano ${picked.lead.data.plan}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => choose(null)}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Trocar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder="Buscar cliente, CPF ou placa do formulário de vendas"
              className="pl-8"
            />
          </div>
          {open && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-border bg-background">
              {isLoading ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Carregando…</p>
              ) : filtered.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Nenhum veículo encontrado em Vendas.</p>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => choose({ lead: o.lead, vehicle: o.vehicle })}
                    className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{o.lead.data.name || "Sem nome"}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {o.position}
                        {[o.vehicle.model, o.vehicle.plate, o.vehicle.year].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {mine && plateKey(o.vehicle.plate) === mine ? (
                        <span className="font-semibold text-primary">Mesma placa</span>
                      ) : (
                        STAGE_LABELS[o.lead.stage]
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Preenche nome, modelo, marca, ano, placa, cor e plano (IMEI e chip nunca mudam). Se o cliente já foi criado, o
        veículo é vinculado a ele ao salvar.
      </p>
    </div>
  );
}
